import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { OverlaySidebar } from './ui/OverlaySidebar';
import { HamburgerMenu } from './ui/HamburgerMenu';
import { DashboardContainer, MainContent } from '../styles/DashboardStyles';
import { useSidebar } from '../contexts/SidebarContext';
import { isMobileDevice } from '../utils/deviceDetection';

import { useAuthViewModel } from '../viewmodels/AuthViewModel';
import styled from 'styled-components';
import { spacing, colors, shadows, zIndex } from '../styles/designTokens';

// 모바일 헤더 스타일
const MobileHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${colors.semantic.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing[4]};
  z-index: ${zIndex.layoutMobileHeader};
  box-shadow: ${shadows.sm};
  
  /* 데스크톱에서는 숨김 */
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const MobileLogo = styled.img`
  height: 24px;
  width: auto;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const MobileTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.semantic.text.primary};
  margin: 0;
`;

// 메인 레이아웃 컴포넌트
const MainLayout: React.FC = () => {
  const { logout, adminInfo } = useAuthViewModel();
  const { collapsed } = useSidebar();
  const [isOverlaySidebarOpen, setIsOverlaySidebarOpen] = useState(false);
  const navigate = useNavigate();

  // 모바일 디바이스인 경우 모바일 라우트로 리다이렉트
  useEffect(() => {
    if (isMobileDevice()) {
      navigate('/mobile/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    console.log('🔐 MainLayout 로그아웃 시작');
    logout();
  };

  const toggleOverlaySidebar = () => {
    setIsOverlaySidebarOpen(!isOverlaySidebarOpen);
  };

  const closeOverlaySidebar = () => {
    setIsOverlaySidebarOpen(false);
  };

  return (
    <DashboardContainer>
      {/* 모바일 헤더 (768px 이하에서만 표시) */}
      <MobileHeader>
        <MobileHeaderContent>
          <HamburgerMenu
            isOpen={isOverlaySidebarOpen}
            onClick={toggleOverlaySidebar}
            size="sm"
            variant="minimal"
          />
          <MobileLogo
            src="/images/logo.png"
            alt="BRANDINSIGHT Logo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <MobileTitle>BI Admin</MobileTitle>
        </MobileHeaderContent>
      </MobileHeader>

      {/* 데스크톱 사이드바 (768px 이상에서만 표시) */}
      <Sidebar />

      {/* 오버레이 사이드바 (768px 이하에서만 표시) */}
      <OverlaySidebar
        isOpen={isOverlaySidebarOpen}
        onClose={closeOverlaySidebar}
        adminInfo={adminInfo}
        onLogout={handleLogout}
      />

      {/* 메인 콘텐츠 영역 - 중첩된 라우트가 여기에 렌더링됩니다 */}
      <MainContent $sidebarCollapsed={collapsed}>
        <Outlet />
      </MainContent>
    </DashboardContainer>
  );
};

export default MainLayout; 